import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Mail, MapPin, Phone, User } from "lucide-react";
import { PageHero, Reveal } from "../components/site/Motion";
import { ReviewsGrid } from "../components/site/Reviews";
import { ReviewForm } from "../components/site/ReviewForm";
import { ReviewFilter } from "../components/site/ReviewFilter";
import { InquiryForm } from "../components/site/InquiryForm";
import { http, telHref } from "../lib/api";
import { useSettings } from "../lib/settings";

export function ReviewsPage() {
  const { data } = useQuery({ queryKey: ["reviews"], queryFn: async () => (await http.get("/reviews")).data, retry: 1 });
  const [params] = useSearchParams();
  const [division, setDivision] = useState(params.get("division") || "all");
  const items = Array.isArray(data?.items) ? data.items : [];
  const counts = items.reduce((acc, r) => ({ ...acc, [r.division]: (acc[r.division] || 0) + 1 }), {});
  const shown = division === "all" ? items : items.filter((r) => r.division === division);
  const hasSamples = items.some((r) => r.is_sample);
  const intro = hasSamples
    ? "Client feedback across our Truck, Trailer, Marine and Equipment divisions. Reviews labelled “Sample Review” are illustrative examples, not statements from actual clients."
    : "Every review shown here was submitted by a client and published with their permission.";
  return (
    <div data-testid="reviews-page">
      <PageHero eyebrow="Client Reviews" title={["Honest work,", "in their words."]} intro={intro} testid="reviews-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        {items.length > 0 && <ReviewFilter value={division} onChange={setDivision} counts={counts} total={items.length} />}
        {items.length > 0 && !shown.length ? (
          <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="reviews-filter-empty">No reviews for this division yet.</p>
        ) : <ReviewsGrid key={division} reviews={shown} />}
      </section>
      <section className="border-t hairline bg-[var(--ink-2)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow">Worked with us?</p>
            <h2 className="font-display text-5xl mt-5 leading-[1]">Share your <em>experience.</em></h2>
            <p className="text-slate-300 mt-5 leading-relaxed">Reviews are moderated and only published with your consent. Your email is never shown publicly.</p>
          </Reveal>
          <div className="lg:col-span-8"><ReviewForm /></div>
        </div>
      </section>
    </div>
  );
}

export function Contact() {
  const s = useSettings();
  const rows = [
    [MapPin, "Business address", `${s.company_name}\n${s.address_line1}\n${s.address_line2}`, null, "address"],
    [Phone, "Phone", s.phone, telHref(s.phone), "phone"],
    [Mail, "General inquiries", s.info_email, `mailto:${s.info_email}`, "info"],
    [Mail, "Sales", s.sales_email, `mailto:${s.sales_email}`, "sales"],
    [User, `Owner · ${s.owner_name}`, s.owner_email, `mailto:${s.owner_email}`, "owner"],
    [User, `Manager · ${s.manager_name}`, s.manager_email, `mailto:${s.manager_email}`, "manager"],
  ];
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title={["Let's talk about", "your next asset."]} testid="contact-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5 space-y-px bg-white/10 border hairline self-start" data-testid="contact-details">
          {rows.map(([Icon, label, value, href, id]) => (
            <div key={id} className="bg-[var(--ink)] p-6 flex gap-5">
              <Icon className="w-5 h-5 text-gold mt-1 shrink-0" />
              <div className="min-w-0">
                <p className="eyebrow !text-[0.62rem]">{label}</p>
                {href ? (
                  <a href={href} className="block font-display text-xl sm:text-2xl mt-2 hover:text-gold transition-colors break-words" data-testid={`contact-${id}-link`}>{value}</a>
                ) : (
                  <p className="font-display text-xl sm:text-2xl mt-2 whitespace-pre-line" data-testid={`contact-${id}`}>{value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-7">
          <p className="eyebrow mb-4">Send a message</p>
          <ul className="mb-8 space-y-2 text-sm text-slate-300 border-l-2 border-[var(--gold)] pl-4" data-testid="contact-instructions">
            <li>Choose <span className="text-slate-100">General</span> for questions about the company or the sourcing process; it goes to {s.info_email}.</li>
            <li>Choose <span className="text-slate-100">Sales</span> for buying, availability or pricing questions; it goes to {s.sales_email}.</li>
            <li>Asking about a listed asset? Use the inquiry button on that listing so we know the stock number.</li>
            <li>Can't find what you need? <a href="/sourcing" className="text-gold underline">Submit an asset sourcing request</a>.</li>
          </ul>
          <InquiryForm testid="contact-form" showTopic />
        </div>
      </section>
    </div>
  );
}
